import { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { Spacings, breakpoints } from '@styles/index';
import debounce from 'lodash/debounce';
import { createOrder, getLocality } from '@actions/physicalNotes';
import H4 from '@base/H4/H4';
import H6 from '@base/H6/H6';
import TextField from '@base/TextField/TextField';
import Divider from '@base/Divider/Divider';
import Button from '@base/Button/Button';
import NumberField from '@base/NumberField/NumberField';
import CircularLoader from '@base/CircularLoader/CircularLoader';
import AutoCompleteInput from '@base/AutoComplete/AutoComplete';
import { useDispatch } from 'react-redux';
import { useSnackbar } from '@stores/snackbar';
import { notesTypes } from '@actions-constants/index';

const ProgressWrapper = styled.div`
  margin-top: ${Spacings.SPACING_8B};
`;

const StyH4 = styled(H4)`
  margin-bottom: ${Spacings.SPACING_2B};
`;

const StyH6 = styled(H6)`
  margin-bottom: ${Spacings.SPACING_2B};
`;

const TextFieldWrapper = styled.div`
  width: 100%;
  margin-top: ${Spacings.SPACING_6B};
`;

const StyledAutoComplete = styled(AutoCompleteInput)`
  &.MuiAutocomplete-root {
    .MuiFormControl-root {
      box-shadow: none;
    }
  }
`;

const StyledTextField = styled(TextField)`
  &.MuiInputBase-root {
    .Mui-disabled {
      color: initial;
    }
  }
`;

const StyTextFieldWrapper = styled(TextFieldWrapper)`
  margin-left: ${Spacings.SPACING_4B};
  @media only screen and (max-width: ${breakpoints.mobile}) {
    margin-left: 0;
  }
`;

const MultipleTextFieldWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  @media only screen and (max-width: ${breakpoints.mobile}) {
    flex-direction: column;
  }
`;

const StyDivider = styled(Divider)`
  width: 100%;
  margin: ${Spacings.SPACING_10B} auto ${Spacings.SPACING_8B};
  background-color: var(--color-divider);
`;

const StyButton = styled(Button)`
  margin-top: ${Spacings.SPACING_8B};
`;

const languages = [
  { name: 'English', code: 1 },
  { name: 'Hindi', code: 2 }
];

const PhysicalNotesForm = ({ handleSuccessSubmit }) => {
  const [fields, setFields] = useState({
    addr: '',
    street: '',
    city: '',
    state: '',
    country: 'India',
    lang: null
  });
  const [selectedLang, setSelectedLang] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [pin, setPin] = useState(null);
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const { open: showSnackbar } = useSnackbar();

  const fetchLocality = () => {
    if (pin)
      dispatch(getLocality(pin)).then((res) =>
        res?.type === `${notesTypes.FETCH_LOCALITY}_SUCCESS`
          ? setFields({
              ...fields,
              city: res.result.city,
              state: res.result.state
            })
          : setFields({ ...fields, city: '', state: '' })
      );
    else setFields({ ...fields, city: '', state: '' });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const delayedFetchLocality = useCallback(debounce(fetchLocality, 500), [pin]);

  const handlePincode = (e) => {
    setFormErrors({ ...formErrors, [e.target.name]: null });
    setPin(e.target.value);
  };

  const handleChange = (e, value) => {
    setFormErrors({ ...formErrors, [e.target.name]: null });
    setFields({ ...fields, [e.target.name]: e.target.value || value });
  };

  const handleOptionClick = (_, value) => {
    setFormErrors({ ...formErrors, lang: null });
    setSelectedLang(value);
    setFields({ ...fields, lang: value.code });
  };

  const checkErrors = () => {
    const errors = {};
    if (!fields.addr.trim()) errors.addr = 'This field cannot be empty';
    if (!pin) errors.pin = 'This field cannot be empty';
    if (pin && !fields.city && !fields.state)
      errors.pin = 'PIN Code is Incorrect';
    if (!fields.lang) errors.lang = 'This field cannot be empty';

    setFormErrors(errors);
    if (Object.keys(errors).length > 0) return true;
    return false;
  };

  const handleSubmit = async () => {
    const isErrors = checkErrors();
    if (!isErrors) {
      const payload = {
        address: {
          billing_address: fields.addr,
          billing_address_2: fields.street,
          billing_city: fields.city,
          billing_pincode: +pin,
          billing_state: fields.state,
          billing_country: fields.country
        },
        language: fields.lang
      };
      setLoading(true);
      const resp = await dispatch(createOrder(payload));
      setLoading(false);
      if (resp?.type === `${notesTypes.CREATE_NOTES_ORDER}_SUCCESS`) {
        handleSuccessSubmit();
      } else {
        showSnackbar({
          message: resp?.error?.message || 'Something went wrong',
          type: 'error'
        });
      }
    }
  };

  useEffect(() => {
    delayedFetchLocality();
    // Cancel the debounce on useEffect cleanup.
    return delayedFetchLocality.cancel;
  }, [pin, delayedFetchLocality]);

  return (
    <>
      <StyH4>Add your address details</StyH4>
      <TextFieldWrapper>
        <StyH6>House / Flat / Block no. *</StyH6>
        <TextField
          placeholder="Address line 1"
          type="text"
          value={fields.addr}
          name="addr"
          inputProps={{
            autoComplete: 'off'
          }}
          onChange={handleChange}
          helperText={formErrors.addr}
          error={!!formErrors.addr}
          fullWidth
        />
      </TextFieldWrapper>
      <TextFieldWrapper>
        <StyH6>Street</StyH6>
        <TextField
          placeholder="Address line 2"
          type="text"
          value={fields.street}
          name="street"
          inputProps={{
            autoComplete: 'off'
          }}
          onChange={handleChange}
          fullWidth
        />
      </TextFieldWrapper>
      <MultipleTextFieldWrapper>
        <TextFieldWrapper>
          <StyH6>PIN code *</StyH6>
          <NumberField
            placeholder="Enter PIN"
            type="text"
            name="pin"
            inputProps={{
              maxLength: 6,
              inputMode: 'numeric',
              pattern: '[0-9]*',
              autoComplete: 'off'
            }}
            onChange={handlePincode}
            helperText={formErrors.pin}
            error={!!formErrors.pin}
            fullWidth
          />
        </TextFieldWrapper>
        <StyTextFieldWrapper>
          <StyH6>City</StyH6>
          <StyledTextField
            placeholder="City"
            type="text"
            value={fields.city}
            name="city"
            helperText={formErrors.city}
            error={!!formErrors.city}
            disabled
            fullWidth
          />
        </StyTextFieldWrapper>
      </MultipleTextFieldWrapper>
      <MultipleTextFieldWrapper>
        <TextFieldWrapper>
          <StyH6>State</StyH6>
          <StyledTextField
            placeholder="State"
            type="text"
            value={fields.state}
            name="state"
            helperText={formErrors.state}
            error={!!formErrors.state}
            disabled
            fullWidth
          />
        </TextFieldWrapper>
        <StyTextFieldWrapper>
          <StyH6>Country</StyH6>
          <StyledTextField
            type="text"
            value="India"
            name="country"
            disabled
            fullWidth
          />
        </StyTextFieldWrapper>
      </MultipleTextFieldWrapper>
      <StyDivider />
      <StyH4>Other details</StyH4>
      <TextFieldWrapper>
        <StyH6>Language preference for notes *</StyH6>
        <StyledAutoComplete
          disableOpenOnFocus
          autoComplete="off"
          noOptionsText={null}
          placeholder="Select language"
          options={languages}
          value={selectedLang}
          onOptionClick={handleOptionClick}
          getOptionLabel={(option) => option.name}
          error={!!formErrors.lang}
        />
      </TextFieldWrapper>
      {loading ? (
        <ProgressWrapper>
          <CircularLoader />
        </ProgressWrapper>
      ) : (
        <StyButton
          label="Submit"
          variant="filled"
          color="green"
          grid={3}
          onClick={handleSubmit}
        />
      )}
    </>
  );
};

PhysicalNotesForm.propTypes = {
  handleSuccessSubmit: PropTypes.func.isRequired
};

export default PhysicalNotesForm;
